import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import FnAntModal from '../../components/blocks/FnAntModal';
import { TErrorData, TMockupMatchesError, TNameNotUniqueError } from './types';
import { DivFooter } from './styles';
import SameNameItem from './components/SameNameItem/index';
import SameArtworkItem from './components/SameArtworkItem/index';
import SectionLayout from './components/SectionLayout/index';
import FnButton from '../../components/elements/FnButton';
import { FnButtonTypes, FnButtonVariants } from '../../components/elements/FnButton/constants';
import { ErrorCode } from './constants';
import { getNewIds } from './utils';
import { FnAntModalWidth } from '../../components/blocks/FnAntModal/constants';

interface Props {
  data: TErrorData;
  isOpen: boolean;
  onClose: () => void;
  onContinue: (data: string[]) => void;
}

const MockupCreateErrorsModal: FC<Props> = ({ data, isOpen, onClose, onContinue }) => {
  const [blanksIds, setBlanksIds] = useState<string[]>([]);

  const handleContinue = useCallback(() => {
    onContinue([...blanksIds, ...data.passedBlankIds]);
  }, [blanksIds, data.passedBlankIds]);

  const handleCheck = useCallback(
    (id: string, value: boolean) => {
      const newIds = getNewIds(blanksIds, id, value);

      setBlanksIds([...newIds]);
    },
    [blanksIds, setBlanksIds]
  );

  const isActive = useMemo(() => {
    return data.passedBlankIds.length !== 0 || blanksIds.length !== 0;
  }, [data, blanksIds]);

  return (
    data && (
      <FnAntModal
        isOpen={isOpen}
        width={FnAntModalWidth.Xl}
        onClose={onClose}
        okText="Continue"
        onOk={handleContinue}
        okDisabled={!isActive}
        cancelText="Cancel"
        onCancel={onClose}
      >
        {Array.isArray(data.errorList) &&
          data.errorList.map((error) => (
            <div key={error.errorCode}>
              {error.errorCode === ErrorCode.RejectedBlank && <SectionLayout errorCode={ErrorCode.RejectedBlank} />}

              {error.errorCode === ErrorCode.NameNotUnique && (
                <SectionLayout errorCode={ErrorCode.NameNotUnique} count={(error as TNameNotUniqueError).data.length}>
                  {(error as TNameNotUniqueError).data &&
                    (error as TNameNotUniqueError).data.map(
                      ({ extendedName, createdDate, creatorUser, thumbnailImageUri }) => (
                        <SameNameItem
                          key={extendedName}
                          extendedName={extendedName}
                          createdDate={createdDate}
                          creatorUser={creatorUser?.title}
                          uri={thumbnailImageUri}
                        />
                      )
                    )}
                </SectionLayout>
              )}

              {error.errorCode === ErrorCode.MockupConfigurationMatches && (
                <SectionLayout
                  errorCode={ErrorCode.MockupConfigurationMatches}
                  count={(error as TMockupMatchesError).data.length}
                >
                  {(error as TMockupMatchesError).data &&
                    (error as TMockupMatchesError).data.map(({ extendedName, thumbnailImageUri, id }, index) => (
                      <SameArtworkItem
                        key={extendedName}
                        extendedName={extendedName}
                        id={id}
                        uri={thumbnailImageUri}
                        onCheck={handleCheck}
                      />
                    ))}
                </SectionLayout>
              )}
            </div>
          ))}
      </FnAntModal>
    )
  );
};

export default memo(MockupCreateErrorsModal);
